import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { Card } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { brl } from "./PurchaseDialog";

const CLS = { pending: "text-amber-500", approved: "text-emerald-500", rejected: "text-rose-500", held: "text-amber-500",
              completed: "text-emerald-500", cancelled: "text-rose-500" };
const dt = (s) => new Date(s).toLocaleString("pt-BR");

const Companies = ({ items, act }) => (
  <div className="space-y-3" data-testid="admin-pc-list">
    {items.length === 0 && <p className="text-sm text-muted-foreground">Nenhum cadastro com taxa paga ainda.</p>}
    {items.map((c) => (
      <div key={c.id} className={`p-4 rounded-xl border ${c.status === "pending" ? "border-amber-500/50 bg-amber-500/5" : "border-border"}`} data-testid={`admin-pc-${c.id}`}>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="font-medium flex flex-wrap items-center gap-2">{c.name}
              <Badge variant="outline" className={CLS[c.status]}>{c.status_label}</Badge>{!c.active && <Badge variant="outline">Oculto</Badge>}</div>
            <div className="text-xs text-muted-foreground">{c.service_type} · {c.state} · desconto {c.discount_percent}% · por {c.owner_name} · {dt(c.created_at)}</div>
            <div className="text-xs mt-1">{c.description}</div>
            {c.youtube_url && <a href={c.youtube_url} target="_blank" rel="noreferrer" className="text-xs text-sky-500 underline">Vídeo</a>}
          </div>
          <div className="flex gap-2 shrink-0">
            {c.status === "pending" && (<>
              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => act(c, "approve")} data-testid={`admin-pc-approve-${c.id}`}>Aprovar</Button>
              <Button size="sm" variant="outline" className="text-rose-500" onClick={() => act(c, "reject")} data-testid={`admin-pc-reject-${c.id}`}>Rejeitar</Button>
            </>)}
            {c.status === "approved" && (
              <Button size="sm" variant="outline" onClick={() => act(c, c.active ? "hide" : "show")} data-testid={`admin-pc-toggle-${c.id}`}>{c.active ? "Ocultar" : "Exibir"}</Button>
            )}
          </div>
        </div>
      </div>
    ))}
  </div>
);

const Table = ({ head, rows, testid, minW = "min-w-[640px]" }) => (
  <div className="overflow-x-auto"><table className={`w-full text-sm ${minW}`} data-testid={testid}>
    <thead className="text-xs uppercase tracking-wider text-muted-foreground"><tr className="border-b border-border">{head.map((h, i) => <th key={h} className={`py-2 ${i > 2 ? "text-right" : "text-left"}`}>{h}</th>)}</tr></thead>
    <tbody>{rows}</tbody></table></div>
);

export const AdminPartnerCompanies = () => {
  const [companies, setCompanies] = useState([]);
  const [fees, setFees] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const load = useCallback(() => {
    api.get("/admin/partner-companies").then((r) => setCompanies(r.data)).catch((e) => toast.error(formatApiError(e)));
    api.get("/admin/partner-fees").then((r) => setFees(r.data)).catch(() => {});
    api.get("/admin/partner-purchases").then((r) => setPurchases(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (c, action) => {
    let reason = "";
    if (action === "reject") { reason = window.prompt("Motivo da rejeição (a taxa de R$ 4,99 será devolvida ao saldo do usuário):"); if (reason === null) return; }
    try { await api.post(`/admin/partner-companies/${c.id}/review`, { action, reason }); toast.success("Atualizado"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const ppAct = async (p, action) => {
    if (!window.confirm(action === "release" ? "Liberar o valor para a empresa e o desconto para o comprador?" : "Cancelar e devolver o valor ao comprador?")) return;
    try { await api.post(`/admin/partner-purchases/${p.id}`, { action }); toast.success("Atualizado"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const paidFees = fees.filter((f) => f.status === "paid");

  return (
    <div className="space-y-6" data-testid="admin-partner-companies">
      <Card className="p-6">
        <div className="font-display text-lg font-bold">Cadastros de empresas parceiras</div>
        <p className="text-xs text-muted-foreground mb-4">Pendentes aparecem destacados. Rejeitar devolve a taxa ao saldo do usuário.</p>
        <Companies items={companies} act={act} />
      </Card>
      <Card className="p-6">
        <div className="font-display text-lg font-bold mb-1">Compras com parceiros</div>
        <p className="text-xs text-muted-foreground mb-4">Em análise até o comprador validar. Você pode liberar ou cancelar (estorno) as que estão em análise.</p>
        <Table testid="admin-pp-list" head={["Data", "Compra", "Comprador", "Valor", "Divisão", "Status"]} minW="min-w-[820px]" rows={purchases.map((p) => (
          <tr key={p.id} className="border-b border-border/50" data-testid={`admin-pp-${p.id}`}>
            <td className="py-2 text-xs text-muted-foreground">{dt(p.created_at)}</td>
            <td>#{p.code} · {p.company_name}<div className="text-[11px] text-muted-foreground">{p.method_label} · desconto {p.discount_percent}%</div></td>
            <td>{p.buyer_name}</td>
            <td className="text-right font-mono">{brl(p.amount)}</td>
            <td className="text-right text-[11px] font-mono">Empresa {brl(p.split.company)} · Patrocinador {p.indicator_name ? `(${p.indicator_name}) ` : "(sem — fica p/ Conecta) "}{brl(p.split.indicator)}<br />Comprador {brl(p.split.cashback)} · Conecta {brl(p.split.platform)}</td>
            <td className="text-right"><span className={`text-xs ${CLS[p.status]}`}>{p.status_label}</span>
              {p.status === "held" && <div className="flex justify-end gap-2 mt-1">
                <button className="text-xs text-emerald-500 underline" onClick={() => ppAct(p, "release")} data-testid={`admin-pp-release-${p.id}`}>Liberar</button>
                <button className="text-xs text-rose-500 underline" onClick={() => ppAct(p, "cancel")} data-testid={`admin-pp-cancel-${p.id}`}>Cancelar</button></div>}</td>
          </tr>))} />
        {purchases.length === 0 && <p className="text-sm text-muted-foreground mt-2">Nenhuma compra ainda.</p>}
      </Card>
      <Card className="p-6">
        <div className="flex flex-wrap justify-between gap-2 mb-4">
          <div className="font-display text-lg font-bold">Taxas de cadastro (R$ 4,99)</div>
          <div className="text-xs text-muted-foreground" data-testid="admin-fees-total">{paidFees.length} pagas · Total: <b className="font-mono text-foreground">{brl(paidFees.reduce((a, f) => a + f.amount, 0))}</b></div>
        </div>
        <Table testid="admin-fees-list" head={["Data", "Usuário", "Empresa", "Valor", "Forma", "Status"]} rows={fees.map((f) => (
          <tr key={f.id} className="border-b border-border/50" data-testid={`admin-fee-${f.id}`}>
            <td className="py-2 text-xs text-muted-foreground">{dt(f.created_at)}</td><td>{f.user_name}</td><td>{f.partner_name}</td>
            <td className="text-right font-mono">{brl(f.amount)}</td><td className="text-right">{f.method_label}</td>
            <td className={`text-right text-xs ${f.status === "refunded" ? "text-rose-500" : "text-emerald-500"}`}>{f.status_label}</td>
          </tr>))} />
        {fees.length === 0 && <p className="text-sm text-muted-foreground mt-2">Nenhuma taxa paga ainda.</p>}
      </Card>
    </div>
  );
};
